import { FC } from "react";
import { useFormContext } from "react-hook-form";
import { DropDownData } from "../../../../../api/services/common/models";
import {
  FormController,
  MuiGrid,
  MuiSelect,
  MuiSelectWithTextInput,
  OptionsDataType,
} from "../../../../../components";
import { MAX_LENGTH_50, MAX_LENGTH_75 } from "../../../../../helpers/constants";
import { setApiDataToDropDownOption } from "../../../../../helpers/utils";
import { useGridContainer } from "../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { ProgramSearchProgramFormKey } from "../../../modelsAndSchemas/models";
import { programSearchSiteCoreKeys } from "../../../siteCore/SiteCoreKeys";
import { ParticipantsNameFields } from "../participantsNameFields/ParticipantsNameFields";

interface NISSearchFieldsProps {
  pageId: string;
  resetForm: boolean;
  filterDropdownValue: OptionsDataType[];
  dedicatedProjectManagerMappedData: OptionsDataType[];
  countryListData: DropDownData[];
}
export const NISSearchFields: FC<NISSearchFieldsProps> = ({
  pageId,
  resetForm,
  filterDropdownValue,
  dedicatedProjectManagerMappedData,
  countryListData,
}) => {
  const { gridSizeLarge: gridSize, gridSizeLargeClass } = useGridContainer();
  const { control } = useFormContext();

  const { commonLabels, fetchDropdownValues, useFetchLabel, commonValidationMessages } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();

  const getLabel = useFetchLabel(programSearchSiteCoreKeys.searchFormKey);

  const { nisId, clientEmployer, nisStatus } = programSearchSiteCoreKeys;

  const nisStatusDropdownValue = fetchDropdownValues(
    programSearchSiteCoreKeys.searchFormKey,
    programSearchSiteCoreKeys.nisStatusList,
  );

  return (
    <MuiGrid container className="form-container">
      <ParticipantsNameFields
        filterDropdownValue={filterDropdownValue}
        pageId={pageId}
        firstNameMaxLength={MAX_LENGTH_75}
        lastNameMaxLength={MAX_LENGTH_75}
      />
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.nisId}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(nisId)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.nisIdFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.nisId}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.nisIdFilter}`}
            maxLength={MAX_LENGTH_50}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.clientEmployer}
        >
          <MuiSelectWithTextInput
            options={filterDropdownValue}
            label={getLabel(clientEmployer)}
            placeholder={commonLabels.inputPlaceHolder}
            control={control}
            selectBoxName={ProgramSearchProgramFormKey.clientEmployerFilter}
            id={`${pageId}-${ProgramSearchProgramFormKey.clientEmployer}`}
            selectBoxId={`${pageId}-${ProgramSearchProgramFormKey.clientEmployerFilter}`}
            maxLength={MAX_LENGTH_75}
            requiredARIALabel={commonARIALabel.required}
            tooltipMessage={
              commonValidationMessages.fieldSpecialCharactersTooltipMessage
            }
            tooltipHeaderTitle={
              commonValidationMessages.allowedSpecialCharactersTitle
            }
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.nisStatus}
        >
          <MuiSelect
            label={getLabel(nisStatus)}
            options={nisStatusDropdownValue}
            visibleDefaultSelectOption
            value=""
            id={`${pageId}-${ProgramSearchProgramFormKey.nisStatus}`}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.projectManager}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.projectManager}`}
            label={getLabel(programSearchSiteCoreKeys.projectManagerLabel)}
            value=""
            options={dedicatedProjectManagerMappedData}
            showSearch
            visibleDefaultSelectOption
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
          />
        </FormController>
      </MuiGrid>
      <MuiGrid size={gridSize} className={gridSizeLargeClass}>
        <FormController
          control={control}
          name={ProgramSearchProgramFormKey.country}
        >
          <MuiSelect
            id={`${pageId}-${ProgramSearchProgramFormKey.country}`}
            label={getLabel(programSearchSiteCoreKeys.countrySearchLabel)}
            visibleDefaultSelectOption
            options={setApiDataToDropDownOption(countryListData, true)}
            placeholder={commonLabels.selectPlaceHolder}
            requiredARIALabel={commonARIALabel.required}
            showSearch
            searchPlaceholder={commonLabels.search}
          />
        </FormController>
      </MuiGrid>
    </MuiGrid>
  );
};
